/**
 * Pure description of the crystal-particle effect drawn over a recording.
 *
 * The effect is recomputed from the note timeline on every frame rather than
 * being simulated statefully, so the same recording always renders identically
 * and a dropped frame cannot desynchronise it from the audio.
 */
import type { NoteEvent } from "../helpers/recordingHelpers";

export interface Particle {
  x: number;
  y: number;
  size: number;
  opacity: number;
}

export interface ParticleFieldConfig {
  /** Y coordinate of the top of the keys on the master canvas. */
  pianoTopY: number;
  whiteKeyWidth: number;
  noteToX: Record<number, number>;
}

/** How long a note-on keeps spawning/animating particles. */
export const PARTICLE_BURST_DURATION_MS = 1800;
const MAX_SPAWN_DELAY_MS = 200;
const MIN_PARTICLE_LIFETIME_MS = 1200;
const EXTRA_PARTICLE_LIFETIME_MS = 600;

/**
 * Deterministic pseudo-random source seeded from the note and its timestamp,
 * so particle positions depend only on the recording, never on frame timing.
 */
const makeRng = (seedBase: number) => (offset: number) => {
  const s = (seedBase * 1664525 + offset * 22695477) & 0xffffffff;
  return (s >>> 0) / 0xffffffff;
};

const seedFor = (event: NoteEvent) =>
  (event.note * 137 + Math.round(event.timeMs)) & 0xffff;

/** Particles visible at `currentTimeMs`, given a chronologically sorted timeline. */
export const computeParticlesAt = (
  sortedEvents: readonly NoteEvent[],
  currentTimeMs: number,
  config: ParticleFieldConfig,
): Particle[] => {
  const particles: Particle[] = [];

  for (const event of sortedEvents) {
    if (event.timeMs > currentTimeMs) break;
    if (event.type !== "on") continue;

    const age = currentTimeMs - event.timeMs;
    if (age > PARTICLE_BURST_DURATION_MS) continue;

    const x = config.noteToX[event.note];
    if (x === undefined) continue;

    const seedBase = seedFor(event);
    const rng = makeRng(seedBase);
    const count = 5 + (seedBase % 5); // 5–9 particles per note

    for (let p = 0; p < count; p++) {
      const particleSeed = p * 31337;
      const spawnDelay = rng(particleSeed) * MAX_SPAWN_DELAY_MS;
      const particleAge = age - spawnDelay;
      if (particleAge < 0) continue;

      const lifetime =
        MIN_PARTICLE_LIFETIME_MS + rng(particleSeed + 1) * EXTRA_PARTICLE_LIFETIME_MS;
      if (particleAge > lifetime) continue;

      const progress = particleAge / lifetime;
      const driftX = (rng(particleSeed + 2) - 0.5) * 60; // px/s
      const riseSpeed = 120 + rng(particleSeed + 3) * 140; // px/s
      const spawnX = x + (rng(particleSeed + 4) - 0.5) * config.whiteKeyWidth * 2;
      const ageSeconds = particleAge / 1000;

      particles.push({
        x: spawnX + driftX * ageSeconds,
        y: config.pianoTopY - riseSpeed * ageSeconds,
        size: 5 + rng(particleSeed + 5) * 7,
        opacity: (1 - progress) * (1 - progress), // quadratic fade
      });
    }
  }

  return particles;
};

/** Opacity and blur of the glow line sitting along the top of the keys. */
export const computeGlowIntensity = (activeNoteCount: number) =>
  activeNoteCount > 0
    ? { alpha: 0.95, blur: 28 }
    : { alpha: 0.35, blur: 10 };
